// Spinner — minimal rotating loader matching the project's mid-century palette.
// Uses a partial border ring (cream base, rust accent at the top) animated via
// tailwind's `animate-spin`. CSS-only — no SVG, no extra DOM.
//
// Variants:
//   <Spinner />                  — 32px, no label
//   <Spinner size="sm" />        — 20px, for inline use next to a label
//   <Spinner size="lg" />        — 48px, for full-screen page-boot states
//   <Spinner label="…" />        — visible caption rendered below the ring
//
// Always emits an aria-label so screen-readers announce the busy state even
// when there's no visible caption.

import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

const SIZE_TO_BOX: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-5 w-5 border-2',
  md: 'h-8 w-8 border-[2.5px]',
  lg: 'h-12 w-12 border-[3px]',
};

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  /** Optional visible caption rendered below the spinner. */
  label?: string;
}

export function Spinner({ size = 'md', label, className, ...rest }: SpinnerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
      className={cn('inline-flex flex-col items-center gap-3', className)}
      {...rest}
    >
      <div
        aria-hidden="true"
        className={cn('animate-spin rounded-full border-line border-t-rust', SIZE_TO_BOX[size])}
      />
      {label && (
        <p className="text-[12px] font-mono uppercase tracking-[0.14em] text-charcoal-soft">
          {label}
        </p>
      )}
    </div>
  );
}

/**
 * Full-viewport centered Spinner — for top-level boot states like the auth
 * loading check on protected routes. Renders nothing else so the underlying
 * page chrome doesn't compete for attention.
 */
export function PageSpinner({ label }: { label?: string }) {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-cream">
      <Spinner size="lg" label={label} />
    </div>
  );
}
